import { jsPDF } from "jspdf";
import { mapquestFetch, incrementTransaction } from "./MapQuestFetch";
import { capitalize } from '../../components/common/capitalize';

const IRS_RATE = 0.7;

/** helper: turn a static‑map URL into base‑64 */
const loadImageAsBase64 = url =>
    new Promise(resolve => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            canvas.getContext("2d").drawImage(img, 0, 0);
            resolve(canvas.toDataURL("image/jpeg"));
        };
        img.onerror = () => resolve("");
        img.src = url;
    });

/* place near the top of the file, before generatePDFWithJsPDF */
const toAmPm = (hhmm = "") => {
    if (!/^\d\d:\d\d$/.test(hhmm)) return hhmm;         // leave unknown formats
    let [h, m] = hhmm.split(":").map(Number);
    const suffix = h >= 12 ? "PM" : "AM";
    h = ((h + 11) % 12) + 1;                            // 0→12, 13→1 PM, etc.
    return `${h}:${String(m).padStart(2, "0")} ${suffix}`;
};


function formatDateMMDDYYYY(dateStr) {
    const [year, month, day] = dateStr.split('-').map(Number);
    const dateObj = new Date(year, month - 1, day); // mes base 0
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    const dd = String(dateObj.getDate()).padStart(2, '0');
    const yyyy = dateObj.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
}


export const generatePDFWithJsPDF = async (entries, userLoggedIn) => {
    /** ▸ 1. sort all raw logs */
    const sortedEntries = [...entries].sort((a, b) => {
        const aDate = `${a.travel_date} ${a.time_in}`;
        const bDate = `${b.travel_date} ${b.time_in}`;
        return new Date(aDate) - new Date(bDate);
    });

    /** ▸ 2. group them into closed trips (start == end) */
    const groupedTrips = [];
    let currentTrip = [];
    let currentStart = null;
    let currentDate = null;
    const tripCounterByDate = {};

    for (let i = 0; i < sortedEntries.length; i++) {
        const e = sortedEntries[i];
        if (!e.start_address || !e.end_address || !e.travel_date) continue;

        if (currentTrip.length === 0) {
            currentStart = e.start_address;
            currentDate = e.travel_date;
        }
        currentTrip.push(e);

        const isTripEnd = e.end_address === currentStart;
        const lastInput = i === sortedEntries.length - 1;

        if (isTripEnd || lastInput) {
            tripCounterByDate[currentDate] =
                (tripCounterByDate[currentDate] || 0) + 1;

            groupedTrips.push({
                date: currentDate,
                tripIndex: tripCounterByDate[currentDate],
                entries: [...currentTrip]
            });

            currentTrip = [];
            currentStart = null;
            currentDate = null;
        }
    }

    /** ▸ 3. create the PDF */
    const doc = new jsPDF({ unit: "pt", format: "letter", orientation: "portrait" });
    let y = 40;

    doc.setFontSize(16);
    doc.text("Mileage Route Report", 40, y);
    y += 20;

    doc.setFontSize(12);
    doc.text(`Full Name: ${capitalize(userLoggedIn?.first_name) || ''} ${capitalize(userLoggedIn?.last_name) || ''}`, 40, y);
    y += 20;

    doc.setFontSize(14);
    let globalLine = 0;

    /** iterate over trips */
    for (const trip of groupedTrips) {
        const { date, tripIndex, entries: tripEntries } = trip;

        /* ───────────────────────── 3.1 section header */
        const randMuted = () => Math.floor(30 + Math.random() * 150);
        const color = { r: randMuted(), g: randMuted(), b: randMuted() };

        doc.setDrawColor(color.r, color.g, color.b);
        doc.setLineWidth(4);
        y += 10;

        const firstEntry = tripEntries[0];
        const lastEntry = tripEntries[tripEntries.length - 1];

        /* ▼ calculate line numbers for this trip */
        const startLine = globalLine + 1;
        const endLine = globalLine + tripEntries.length;

        doc.setFontSize(13);
        doc.setTextColor(color.r, color.g, color.b);
        doc.text(
            `Trip #${tripIndex} | Date:  ${formatDateMMDDYYYY(date)} | Time: ` +
            `${toAmPm(firstEntry.time_in)} – ${toAmPm(lastEntry.time_out)} ` +
            `(Line ${startLine} to ${endLine})`,
            40,
            y
        );
        doc.setTextColor(0, 0, 0);
        y += 10;
        doc.line(40, y, 550, y);
        y += 20;


        /* ───────────────────────── 3.2 build location list */
        const sanitize = s => (typeof s === "string" ? s.trim() : "");
        const allLocations = [
            sanitize(firstEntry.start_address),
            ...tripEntries.slice(0, -1).map(e => sanitize(e.end_address)),
            sanitize(lastEntry.end_address)
        ];

        /* ───────────────────────── 3.3 ONE MapQuest call for the whole trip */
        const routeRes = await mapquestFetch(
            "https://www.mapquestapi.com/directions/v2/route?key=4H727f3Nq5ymaqNEeszFgjGOtY0tkiK7",
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ locations: allLocations, options: { unit: "m" } })
            }
        );
        const routeJson = await routeRes.json();
        incrementTransaction();

        const route = routeJson?.route;
        if (!route) continue; // skip on failure

        /* 3.3.a bounding‑box guard */
        let boundingBoxParam = "";
        if (route.boundingBox) {
            const { ul, lr } = route.boundingBox;
            const latSpan = Math.abs(ul.lat - lr.lat);
            const lngSpan = Math.abs(ul.lng - lr.lng);
            if (latSpan > 0.0001 || lngSpan > 0.0001) {
                boundingBoxParam = `&boundingBox=${ul.lat},${ul.lng},${lr.lat},${lr.lng}`;
            }
            // else let MapQuest auto‑zoom
        }

        /* ───────────────────────── 3.4 per‑segment output */
        const legs = route.legs || [];

        for (let i = 0; i < tripEntries.length; i++) {
            const log = tripEntries[i];
            const leg = legs[i] || {}; // might be undefined if API truncated
            const maneuvers = leg.maneuvers || [];

            /* 3.4.a log header */
            doc.text(`From: ${log.start_address}`, 40, y); y += 15;
            doc.text(`To:   ${log.end_address}`, 40, y); y += 15;
            doc.text(`Miles: ${Number(log.mileage).toFixed(2)}`, 40, y); y += 15;
            doc.text(
                `IRS reimbursement: (${Number(log.mileage).toFixed(2)} × 0.70)  $${(
                    parseFloat(log.mileage) * IRS_RATE
                ).toFixed(2)}`,
                40,
                y
            );
            y += 20;

            if (y > 730) {
                doc.addPage();
                y = 40;
            }

            /* 3.4.b segment directions */
            doc.setFontSize(11);
            doc.text("Directions:", 40, y); y += 14;

            for (const step of maneuvers) {
                const raw = `${step.narrative} (${step.distance.toFixed(2)} mi)`;
                const wrapped = doc.splitTextToSize(raw, 480); // 480pt width
                for (let j = 0; j < wrapped.length; j++) {
                    if (y > 750) { doc.addPage(); y = 40; }
                    const prefix = j === 0 ? "• " : "   ";
                    doc.text(prefix + wrapped[j], 50, y);
                    y += 14;
                }
            }
            doc.setFontSize(13);
            y += 10;
        }

        y += 10;

        /* ───────────────────────── 3.5 static map snapshot */
        const joinedLocations = allLocations.map(encodeURIComponent).join("||");
        const staticMapUrl =
            "https://www.mapquestapi.com/staticmap/v5/map" +
            `?key=4H727f3Nq5ymaqNEeszFgjGOtY0tkiK7` +
            `&session=${route.sessionId}` +
            boundingBoxParam +
            `&size=600,400` +
            `&defaultMarker=marker-sm-num` +
            `&locations=${joinedLocations}`;

        const base64Map = await loadImageAsBase64(staticMapUrl);

        const margin = 40;
        const maxW = 400;
        const maxH = 200;
        await new Promise(resolve => {
            const img = new Image();
            img.src = base64Map;
            img.onload = () => {
                const ratio = img.width / img.height;
                let w = maxW;
                let h = w / ratio;
                if (h > maxH) { h = maxH; w = h * ratio; }
                if (y + h > doc.internal.pageSize.getHeight() - margin) {
                    doc.addPage();
                    y = margin;
                }
                doc.addImage(base64Map, "JPEG", margin, y, w, h);
                y += h + 10;
                resolve();
            };
        });
        globalLine = endLine;
    }

    doc.save("route-report.pdf");
};
